import { EventTypes } from '@candulabs/craft';
import { useEffect } from 'react';

import { BuiltInComponents } from 'craft/constants';
import { useEditor, useSelection } from 'craft/hooks';

import { useFlow } from './useFlow';

export const useFlowSelectionManager = (nodeId: string) => {
  const { actions, query } = useEditor();
  const { activeStepId } = useFlow(nodeId);

  const { all } = useSelection().selection;

  // if navigation shortcuts lands us on a flowStepId that is different than the one currently set
  useEffect(() => {
    if (all.length !== 1) {
      return;
    }
    const selectedNode = query.node(all[0]);
    const ancestors = selectedNode ? selectedNode.ancestors(true) : [];
    const flowStepNodeId = [all[0], ...ancestors]?.find(
      (n) => query.node(n)?.get()?.data?.name === BuiltInComponents.FlowStep,
    );

    if (!flowStepNodeId) {
      return;
    }

    const selectedNodeFlowStepId = query.node(flowStepNodeId)?.get()?.data.props.stepId;
    if (selectedNodeFlowStepId === activeStepId) {
      return;
    }
    /**
     *  if a selected node has a flowStepId different to the state one,
     *  the state one should be used instead for re-selecting the proper flowStep node last deep descendant
     *  */
    const flowElement = query.node(flowStepNodeId).getParent();

    const correspondingFlowStepToSelect = flowElement
      .descendants(true)
      .find((node) => query.node(node).get().data.props.stepId === activeStepId);

    const lastItemOfSelectedFlowStep = query.node(correspondingFlowStepToSelect)?.descendants(true);
    if (correspondingFlowStepToSelect) {
      actions.setEventNodes(
        EventTypes.selected,
        lastItemOfSelectedFlowStep[lastItemOfSelectedFlowStep.length - 1],
      );
    }
  }, [all, query, activeStepId]);

  if (all.length !== 1) {
    return {
      isFlowDescendantSelected: false,
      isFlowStepSelected: false,
    };
  }

  // Flow and FlowStep
  const isFlowStepSelected =
    query.node(all[0])?.get()?.data?.name === BuiltInComponents.FlowStep ||
    query.node(all[0])?.get()?.data?.name === BuiltInComponents.Flow;

  const isFlowDescendantSelected =
    query
      .node(all[0])
      ?.ancestors(true)
      ?.findIndex((n) => n === nodeId) > -1;

  return {
    isFlowDescendantSelected,
    isFlowStepSelected,
  };
};
